import cx from 'clsx';
import { useCurrentWidth } from 'hooks/dom';
import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import { ACFMedia } from 'types/media';
import { generateImageSrcset } from 'utils/images';
import styles from './Image.module.scss';
import LazyImage from './LazyImage';

export interface ImageSpecificProps {
  componentHeight?: boolean;
}

interface OwnProps {
  acfConfig?: ACFMedia;
  wrapperClassName?: string;
  priority?: boolean;
}

type ImageProps = Omit<JSX.IntrinsicElements['img'], 'ref'> & OwnProps & ImageSpecificProps;

const Image: FunctionComponent<ImageProps> = props => {
  const { className, wrapperClassName, acfConfig, sizes = '100vw', componentHeight = false, width, height, priority, ...rest } = props;

  const containerRef = useRef<HTMLDivElement>();
  const [componentWidth, setComponentWidth] = useState(0);
  const windowWidth = useCurrentWidth();

  const { sizes: allSizes, alt = '', mime_type, url } = acfConfig || {};
  const { small } = allSizes || {};
  const isSVG = mime_type === 'image/svg+xml';
  let srcSet;
  let imageHeight = 0;

  const wrapperRef = useCallback(node => {
    if (node) {
      containerRef.current = node;
      setComponentWidth(node.offsetWidth);
    }
  }, []);

  useEffect(() => {
    if (containerRef?.current) {
      setComponentWidth(containerRef.current.offsetWidth);
    }
  }, [windowWidth]);

  if (height && width && (!allSizes || isSVG)) {
    const aspectRatio = Number(width) / Number(height);
    imageHeight = componentWidth / aspectRatio;
  } else if (isSVG) {
    imageHeight = 0;
    srcSet = generateImageSrcset({ url, sizes: allSizes });
  } else if (allSizes) {
    const aspectRatio = allSizes['standard-width'] / allSizes['standard-height'];
    imageHeight = componentWidth / aspectRatio;
    srcSet = generateImageSrcset({ url, sizes: allSizes });
  }

  const imageClassName = cx({
    [styles.image]: true,
    [styles.priority]: priority,
    ...(className && { [className]: true }),
  });

  return (
    <div className={imageClassName} ref={wrapperRef} {...(imageHeight && !componentHeight && { style: { height: `${imageHeight}px` } })}>
      <LazyImage
        // {...(isSVG && { 'aria-hidden': true })}
        {...(srcSet && { srcSet })}
        {...(sizes && { sizes })}
        src={small || ''}
        {...(imageHeight !== 0 && !componentHeight && { height: imageHeight })}
        width={componentWidth}
        alt={alt}
        {...(componentHeight && { style: { height: '100%' } })}
        priority={priority}
        {...rest}
      />
    </div>
  );
};

export default Image;
